import React, { useCallback, useState } from 'react';

import dlv from 'dlv';
import { defineMessages, useIntl } from 'react-intl';

import { IBaseProps } from '@rbi-ctg/frontend';
import IconDeleteCredit from 'components/icons/delete-credit';
import LoadingAnimation from 'components/loading-animation';
import SelectionIndicator from 'components/selection-indicator';
import { Size } from 'components/selection-indicator/constants';
import { Tooltip } from 'components/tooltip';
import useDialogModal from 'hooks/use-dialog-modal';
import { CardTypes, ICredit, IPaymentMethod } from 'state/payment/types';
import noop from 'utils/noop';
import { mapPaymentMethodCardTypes } from 'utils/payment/map-payment-method-card-type';

import { cardIconRenderer } from './credit-card-icon';
import PrepaidMethodBalance from './prepaid-method-balance';
import {
  CreditCardAlias,
  DefaultIndicatorContainer,
  EndsInWrapper,
  MethodContainer,
  MethodType,
  MethodTypeWrapper,
  RemoveMethod,
  RemoveMethodWrapper,
  StyledIconPrepaidCard
} from './styled';

export interface IPaymentMethodOptionProps extends IBaseProps {
  method: IPaymentMethod;
  selected?: boolean;
  onClick: (fdAccountId: string) => void;
  showDefault?: boolean;
  removable?: boolean;
  readonly?: boolean;
  deletePaymentMethod?: (fdAccountId: string) => Promise<void>;
  hideDeleteIcon?: boolean;
  disableHover?: boolean;
  disableMethod?: boolean;
  hideBorder?: boolean;
  ariaLabelledby?: string;
}

function PaymentMethodOption({
  className,
  method,
  selected,
  onClick,
  showDefault,
  removable,
  readonly,
  deletePaymentMethod,
  disableHover = false,
  disableMethod = false,
  hideDeleteIcon = false,
  hideBorder = false,
  ariaLabelledby = 'payment-method-loads-with payment-method-label payment-method-card payment-method-alias payment-method-expirationDate'
}: IPaymentMethodOptionProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const { formatMessage } = useIntl();

  const onDeleteMethod = useCallback(() => {
    if (deletePaymentMethod) {
      setIsDeleting(true);
      const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
      deletePaymentMethod(accountIdentifier).finally(() => {
        setIsDeleting(false);
      });
    }
  }, [method, deletePaymentMethod]);

  const onClickMethod = useCallback(() => {
    if (readonly || disableMethod) {
      return;
    }
    if (typeof onClick === 'function') {
      const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
      onClick(accountIdentifier);
    }
  }, [disableMethod, readonly, method, onClick]);

  const [Dialog, openDialog] = useDialogModal({
    onConfirm: onDeleteMethod,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Delete payment method'
  });

  const expiration = (credit: ICredit) => {
    const { expiryYear: expYr, expiryMonth: expMon } = credit || {};
    if (!expYr || !expMon) {
      return;
    }
    const expirationDate = new Date(2000 + parseInt(expYr, 10), parseInt(expMon, 10));
    const currentDate = new Date();
    const isValid = expirationDate.getTime() - currentDate.getTime();
    return (
      <span id="payment-method-expirationDate">
        &nbsp;
        {`${
          isValid ? formatMessage(messages.expire) : formatMessage(messages.expired)
        } ${expMon}/${expYr}`}
      </span>
    );
  };

  const RenderMethodType = () => {
    if (method.credit) {
      const creditCardAlias = (
        <CreditCardAlias>
          <EndsInWrapper>{formatMessage(messages.endsIn).toLowerCase()}</EndsInWrapper>
          <div>{method.credit.alias}</div>
        </CreditCardAlias>
      );
      let isCreditCard;
      let alias;
      let cardType: CardTypes | undefined;
      switch (method.credit.cardType) {
        case CardTypes.APPLE_PAY:
          isCreditCard = false;
          alias = formatMessage(messages.applePay);
          break;
        case CardTypes.GOOGLE_PAY:
          isCreditCard = false;
          alias = formatMessage(messages.googlePay);
          break;
        default:
          cardType = mapPaymentMethodCardTypes(method.credit.cardType);
          isCreditCard = true;
          alias = creditCardAlias;
      }
      return (
        <>
          {isCreditCard && <span id="payment-method-card">{cardType || 'CARD'}</span>}
          <MethodType id="payment-method-alias">{alias}</MethodType>
          {isCreditCard && expiration(method.credit)}
        </>
      );
    }

    if (method.prepaid) {
      return <PrepaidMethodBalance method={method} />;
    }

    return null;
  };

  return isDeleting ? (
    <MethodContainer disableHover={disableHover} hideBorder={hideBorder}>
      <LoadingAnimation fillColor="#000000" />
    </MethodContainer>
  ) : (
    <MethodContainer
      disableHover={disableHover}
      disableMethod={disableMethod}
      hideBorder={hideBorder}
      className={className}
      role="button"
      aria-labelledby={ariaLabelledby}
      tabIndex={0}
      data-testid={`payment-method-${method.accountIdentifier ?? method.fdAccountId ?? ''}`}
    >
      <MethodTypeWrapper
        onClick={onClickMethod}
        data-testid={`method-type-wrapper-${method.accountIdentifier ?? method.fdAccountId ?? ''}`}
      >
        {method.credit && cardIconRenderer(method.credit.cardType)}
        {method.prepaid && <StyledIconPrepaidCard />}
        <RenderMethodType />
        {disableMethod && <span>{formatMessage(messages.paymentMethodNotAvailable)}</span>}
      </MethodTypeWrapper>
      {showDefault && !readonly && (
        <DefaultIndicatorContainer
          onClick={onClickMethod}
          style={{ marginRight: `calc(${removable ? '0' : '1.5rem'} + 1.5rem)` }}
        >
          <SelectionIndicator
            type="radio"
            name={dlv(method, 'credit.alias', dlv(method, 'prepaid.alias'))}
            value={method.accountIdentifier ?? method.fdAccountId ?? ''}
            checked={!!(showDefault && selected)}
            onChange={noop}
            size={Size.REGULAR}
          />
        </DefaultIndicatorContainer>
      )}
      {!hideDeleteIcon && removable && (
        <RemoveMethodWrapper>
          <RemoveMethod
            onClick={openDialog}
            data-testid="remove-card-button"
            aria-label={formatMessage(messages.removePaymentMethod)}
            type="button"
          >
            <Tooltip text={formatMessage(messages.delete)}>
              <IconDeleteCredit />
            </Tooltip>
          </RemoveMethod>
        </RemoveMethodWrapper>
      )}
      <Dialog
        heading={formatMessage(messages.deletePaymentMethod)}
        body={formatMessage(messages.confirmDeletePaymentMethod)}
        modalAppearanceEventMessage="Confirmation: Delete Payment Method"
      />
    </MethodContainer>
  );
}

export default PaymentMethodOption;
const messages = defineMessages({
  confirmDeletePaymentMethod: {
    id: 'confirmDeletePaymentMethod',
    defaultMessage: 'Are you sure you want to delete this payment method?',
    description: 'No description'
  },

  deletePaymentMethod: {
    id: 'deletePaymentMethod',
    defaultMessage: 'Delete payment method',
    description: 'No description'
  },

  delete: {
    id: 'delete',
    defaultMessage: 'delete',
    description: 'No description'
  },

  removePaymentMethod: {
    id: 'removePaymentMethod',
    defaultMessage: 'Remove payment method',
    description: 'No description'
  },

  googlePay: {
    id: 'googlePay',
    defaultMessage: 'Google Pay',
    description: 'No description'
  },

  applePay: {
    id: 'applePay',
    defaultMessage: 'Apple Pay',
    description: 'No description'
  },

  endsIn: {
    id: 'endsIn',
    defaultMessage: 'Ends in',
    description: 'No description'
  },

  expired: {
    id: 'expired',
    defaultMessage: 'Expired',
    description: 'No description'
  },

  expire: {
    id: 'expire',
    defaultMessage: 'exp.',
    description: 'No description'
  },

  paymentMethodNotAvailable: {
    id: 'paymentMethodNotAvailable',
    defaultMessage: 'Not available',
    description: 'The payment method is not available'
  }
});
